import {
  EDITOR_EDITABLE_CANDIDATE_SELECTOR,
  EDITOR_ELEMENT_ID_ATTR,
  PC_CHART_SPEC_ATTR,
  PC_EDITABLE_ATTR,
  PC_OBJECT_ATTR,
  PC_TEXT_FRAME_ATTR,
  PC_TYPE_ATTR,
} from './editor-contract';

/**
 * Editor HTML contract inspector, shared by the backend (jsdom document) and
 * the dashboard (DOMParser document). Callers own the parse and pass the
 * resulting document; every check runs against `document.body`.
 */

const EDITABLE_SELECTOR = EDITOR_EDITABLE_CANDIDATE_SELECTOR;
const OBJECT_SELECTOR = `[${PC_OBJECT_ATTR}="true"]`;

export type UnsafeEditorHtmlIssue =
  | 'fake_table'
  | 'nested_editable_flow'
  | 'non_absolute_object'
  | 'editable_layout_shell'
  | 'nested_editor_object'
  | 'selectable_data_id'
  | 'duplicate_object_id';

export type UnsafeEditorHtmlIssueDetail = {
  type: UnsafeEditorHtmlIssue;
  message: string;
  affectedElementIds?: string[];
  fixHint?: string;
};

export type UnsafeEditorHtmlInspection = {
  safe: boolean;
  issues: UnsafeEditorHtmlIssue[];
  details: UnsafeEditorHtmlIssueDetail[];
};

const XHTML_NAMESPACE = 'http://www.w3.org/1999/xhtml';

// Realm-safe HTMLElement guard (skips SVG/MathML nodes matched by the
// editable selector); `instanceof HTMLElement` breaks across jsdom and
// iframe realms.
function isHtmlElement(element: Element) {
  return element.namespaceURI === XHTML_NAMESPACE;
}

function isEditableCandidate(element: Element) {
  if (element.getAttribute(PC_EDITABLE_ATTR) === 'false') return false;
  return element.matches(EDITABLE_SELECTOR);
}

function isLayoutShell(element: Element) {
  if (!isEditableCandidate(element)) return false;
  if (element.getAttribute(PC_OBJECT_ATTR) === 'true') return false;
  const style = (element.getAttribute('style') ?? '').toLowerCase();
  const hasLayoutDisplay =
    /(?:^|;)\s*display\s*:\s*(flex|grid)\b/.test(style) ||
    /(?:^|;)\s*flex-direction\s*:/.test(style) ||
    /(?:^|;)\s*grid-template/.test(style);
  if (!hasLayoutDisplay) return false;
  return element.querySelector(EDITABLE_SELECTOR) !== null;
}

export function isTopLevelEditorObject(element: Element) {
  if (element.getAttribute(PC_OBJECT_ATTR) === 'true') return true;
  if (!isEditableCandidate(element)) return false;
  const style = (element.getAttribute('style') ?? '').toLowerCase();
  const isAbsolute = /(?:^|;)\s*position\s*:\s*absolute\b/.test(style);
  const parent = element.parentElement;
  return Boolean(isAbsolute && parent && parent.closest(OBJECT_SELECTOR) === null);
}

export function inspectUnsafeEditorDocument(
  document: Document
): UnsafeEditorHtmlInspection {
  const issues = new Set<UnsafeEditorHtmlIssue>();
  const detailKeys = new Set<string>();
  const details: UnsafeEditorHtmlIssueDetail[] = [];

  const elementIds = (element: Element | null | undefined) => {
    const id = element?.getAttribute(EDITOR_ELEMENT_ID_ATTR);
    return id ? [id] : undefined;
  };

  const addIssue = (
    type: UnsafeEditorHtmlIssue,
    input: {
      element?: Element | null;
      affectedElementIds?: string[];
      message: string;
      fixHint?: string;
    }
  ) => {
    issues.add(type);
    const affectedElementIds =
      input.affectedElementIds ?? elementIds(input.element);
    const key = `${type}:${affectedElementIds?.join(',') ?? ''}:${input.message}`;
    if (detailKeys.has(key)) return;
    detailKeys.add(key);
    details.push({
      type,
      message: input.message,
      ...(affectedElementIds?.length ? { affectedElementIds } : {}),
      ...(input.fixHint ? { fixHint: input.fixHint } : {}),
    });
  };

  document.body
    .querySelectorAll(`[${PC_TYPE_ATTR}="table"]`)
    .forEach((element) => {
      if (element.tagName.toLowerCase() !== 'table') {
        addIssue('fake_table', {
          element,
          message: `Element ${element.getAttribute(EDITOR_ELEMENT_ID_ATTR) || element.tagName.toLowerCase()} is marked data-pc-type="table" but is a <${element.tagName.toLowerCase()}>. Editable tables must be real <table> elements.`,
          fixHint:
            'Move data-id, data-pc-type="table", data-pc-object="true", data-pc-editable, and data-pc-lock onto the child <table>, or change the wrapper to data-pc-type="group"/"shape" if the table should not be cell-editable.',
        });
      }
    });

  document.body.querySelectorAll(EDITABLE_SELECTOR).forEach((element) => {
    if (!isHtmlElement(element)) return;
    if (element.getAttribute(PC_EDITABLE_ATTR) === 'false') return;
    const objectAncestor = element.closest(OBJECT_SELECTOR);
    if (objectAncestor && objectAncestor !== element) {
      addIssue('nested_editor_object', {
        element,
        message:
          'Editable/selectable object is nested inside another editor object.',
        fixHint:
          'Keep only top-level selected objects marked editable/object, or make nested content inert normal HTML inside the group.',
      });
      return;
    }
    const style = element.getAttribute('style') ?? '';
    const isAbsolute = /(?:^|;)\s*position\s*:\s*absolute\b/i.test(style);
    const isObject = element.getAttribute(PC_OBJECT_ATTR) === 'true';

    if (!isObject && isLayoutShell(element)) {
      addIssue('editable_layout_shell', {
        element,
        message:
          'A layout shell is marked editable while also containing editable descendants.',
        fixHint:
          'Mark the shell as a group object, or remove editability from the shell and keep only meaningful child objects editable.',
      });
    }

    const isExplicitEditable =
      element.getAttribute(PC_EDITABLE_ATTR) === 'true' ||
      element.hasAttribute(PC_TYPE_ATTR) ||
      element.hasAttribute(PC_TEXT_FRAME_ATTR) ||
      element.hasAttribute(PC_CHART_SPEC_ATTR);

    if (
      isExplicitEditable &&
      !isObject &&
      !isAbsolute &&
      !element.closest(OBJECT_SELECTOR)
    ) {
      addIssue('non_absolute_object', {
        element,
        message:
          'Editable/selectable object is not absolute-positioned or wrapped as a top-level editor object.',
        fixHint:
          'For working HTML, prefer marking composed flex/grid modules as group objects; freeze will convert valid top-level objects to absolute editor geometry.',
      });
    }

    const layoutAncestor = element.parentElement?.closest(
      '[style*="display:flex"],[style*="display: flex"],[style*="display:grid"],[style*="display: grid"]'
    );
    if (
      isExplicitEditable &&
      layoutAncestor &&
      !element.closest(OBJECT_SELECTOR) &&
      layoutAncestor !== document.body
    ) {
      addIssue('nested_editable_flow', {
        element,
        message:
          'Editable/selectable object is nested inside a flex/grid layout flow.',
        fixHint:
          'Use a top-level group object for the composed module, or leave children as normal inert HTML until freeze extracts text/table/chart primitives.',
      });
    }
  });

  document.body
    .querySelectorAll(`[${EDITOR_ELEMENT_ID_ATTR}]`)
    .forEach((element) => {
      if (element.closest(OBJECT_SELECTOR)) return;
      if (
        element.getAttribute(PC_EDITABLE_ATTR) !== 'false' &&
        element.parentElement &&
        element.parentElement !== document.body
      ) {
        addIssue('selectable_data_id', {
          element,
          message:
            'Nested element has data-id and may become unintentionally selectable.',
          fixHint:
            'Reserve data-id for top-level editor objects; remove data-id from inert nested markup unless it is inside a finalized editor object.',
        });
      }
    });

  const objectIds = new Set<string>();
  document.body
    .querySelectorAll(`${OBJECT_SELECTOR}[${EDITOR_ELEMENT_ID_ATTR}]`)
    .forEach((element) => {
      const id = element.getAttribute(EDITOR_ELEMENT_ID_ATTR);
      if (!id) return;
      if (objectIds.has(id)) {
        addIssue('duplicate_object_id', {
          element,
          affectedElementIds: [id],
          message: `Duplicate editor object id "${id}" appears more than once.`,
          fixHint: 'Give every data-pc-object="true" element a unique data-id.',
        });
        return;
      }
      objectIds.add(id);
    });

  return { safe: issues.size === 0, issues: Array.from(issues), details };
}
